import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { getChildOrders } from "../../../api/bseApi";

const cardStyle = {
  background: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  marginBottom: "20px",
};

const teal = "#06d1c6";
const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  border: `1px solid ${teal}`,
  borderRadius: "6px",
  backgroundColor: "#fff",
  color: "#334155",
  fontSize: "14px",
};

/** Format date for BSE (DD/MM/YYYY or DD MMM YYYY per API) */
function formatDateForBse(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

export default function SystematicOrders() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [registrationDateEnabled, setRegistrationDateEnabled] = useState(true);
  const [startDateFrom, setStartDateFrom] = useState("");
  const [startDateTo, setStartDateTo] = useState("");
  const [viewType, setViewType] = useState("onScreen"); // "onScreen" | "xls"
  const [childOrders, setChildOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const handleApply = async (e) => {
    e.preventDefault();
    setLoading(true);
    setApiError(null);
    try {
      const date = startDateFrom || formatDateForBse(new Date());
      const res = await getChildOrders({
        date: date.includes("/") ? date : formatDateForBse(date),
        clientCode: search.trim() || undefined,
        systematicPlanType: status === "ACTIVE" ? "XSIP" : status === "CANCELLED" ? "XSIP" : "XSIP",
        regnNo: undefined,
      });
      if (res.data?.status === 1) {
        const list = res.data?.data || [];
        setChildOrders(Array.isArray(list) ? list : []);
      } else {
        setChildOrders([]);
        setApiError(res.data?.message || res.data?.error || "Failed to load systematic orders");
      }
    } catch (err) {
      setChildOrders([]);
      setApiError(err.response?.data?.message || err.message || "Failed to fetch child orders");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          {/* Breadcrumb and Header */}
          <div className="mb-4" style={cardStyle}>
            <div className="content-page-header content-page-headersplit">
              <h5>Systematic Orders</h5>
            </div>
            <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <NavLink to="/dashboard">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item">
                  <NavLink to="/admin/invest-bse/profile-list">Invest Online BSE</NavLink> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Systematic Orders
                </li>
              </ol>
            </nav>
          </div>

          {/* Filter card */}
          <div style={cardStyle}>
            <form onSubmit={handleApply}>
              {/* Top row: Search, Select Status */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold text-secondary mb-1 d-block">Search:</label>
                  <input
                    type="text"
                    placeholder="Type to Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold text-secondary mb-1 d-block">Select Status:</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    style={inputStyle}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                    <option value="PENDING">PENDING</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </select>
                </div>
              </div>

              {/* Middle row: Registration date toggle, Start Date range */}
              <div className="row g-3 mb-3 align-items-end">
                <div className="col-12 col-md-4">
                  <label className="form-label small fw-semibold text-secondary mb-2 d-block">Registration date</label>
                  <div
                    role="switch"
                    aria-checked={registrationDateEnabled}
                    tabIndex={0}
                    onClick={() => setRegistrationDateEnabled((v) => !v)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setRegistrationDateEnabled((v) => !v);
                      }
                    }}
                    style={{
                      width: 48,
                      height: 26,
                      borderRadius: 13,
                      background: registrationDateEnabled ? teal : "#cbd5e1",
                      cursor: "pointer",
                      position: "relative",
                      transition: "background 0.2s",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: 2,
                        left: registrationDateEnabled ? 24 : 2,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: "#fff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        transition: "left 0.2s",
                      }}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-8">
                  <label className="form-label small fw-semibold text-secondary mb-1 d-block">Start Date</label>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="position-relative d-inline-block" style={{ flex: "1 1 140px", minWidth: 140 }}>
                      <input
                        type="text"
                        placeholder="DD-MM-YYYY"
                        value={startDateFrom}
                        onChange={(e) => setStartDateFrom(e.target.value)}
                        style={{ ...inputStyle, paddingLeft: 36 }}
                        maxLength={10}
                      />
                      <span
                        className="position-absolute"
                        style={{ left: 10, top: "50%", transform: "translateY(-50%)", color: "#64748b", pointerEvents: "none", fontSize: "14px" }}
                        aria-hidden
                      >
                        📅
                      </span>
                    </span>
                    <span style={{ color: "#64748b" }}>-</span>
                    <input
                      type="text"
                      placeholder="DD-MM-YYYY"
                      value={startDateTo}
                      onChange={(e) => setStartDateTo(e.target.value)}
                      style={{ ...inputStyle, flex: "1 1 140px", minWidth: 140 }}
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>

              {/* Bottom row: View Type, Apply */}
              <div className="row g-3 mb-3">
                <div className="col-12">
                  <label className="form-label small fw-semibold text-secondary mb-2 d-block">View Type:</label>
                  <div className="d-flex gap-4">
                    <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="viewType"
                        checked={viewType === "onScreen"}
                        onChange={() => setViewType("onScreen")}
                        style={{ accentColor: teal }}
                      />
                      On Screen
                    </label>
                    <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="viewType"
                        checked={viewType === "xls"}
                        onChange={() => setViewType("xls")}
                        style={{ accentColor: teal }}
                      />
                      XLS
                    </label>
                  </div>
                </div>
              </div>

              <div className="text-center mt-4">
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: teal,
                    color: "#fff",
                    border: "none",
                    padding: "10px 28px",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "15px",
                    cursor: loading ? "wait" : "pointer",
                    opacity: loading ? 0.8 : 1,
                  }}
                >
                  {loading ? "Loading…" : "Apply"}
                </button>
              </div>
            </form>
          </div>

          {apiError && (
            <div className="mb-3 small text-danger" style={cardStyle}>
              {apiError}
            </div>
          )}
          {childOrders.length > 0 && viewType === "onScreen" && (
            <div style={{ ...cardStyle, overflowX: "auto" }}>
              <h6 className="mb-3" style={{ fontWeight: 600, color: "#334155" }}>Systematic / Child Orders</h6>
              <table className="table table-bordered mb-0" style={{ fontSize: "13px" }}>
                <thead style={{ backgroundColor: "#f8fafc" }}>
                  <tr>
                    <th style={{ padding: "10px 12px" }}>Regn No</th>
                    <th style={{ padding: "10px 12px" }}>Client Code</th>
                    <th style={{ padding: "10px 12px" }}>Scheme</th>
                    <th style={{ padding: "10px 12px" }}>Type</th>
                    <th style={{ padding: "10px 12px" }}>Amount</th>
                    <th style={{ padding: "10px 12px" }}>Status</th>
                    <th style={{ padding: "10px 12px" }}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {childOrders.map((row, idx) => (
                    <tr key={row.RegnNo || row.regnNo || idx}>
                      <td style={{ padding: "10px 12px" }}>{row.RegnNo ?? row.regnNo ?? "-"}</td>
                      <td style={{ padding: "10px 12px" }}>{row.ClientCode ?? row.clientCode ?? "-"}</td>
                      <td style={{ padding: "10px 12px" }}>{row.SchemeName ?? row.schemeName ?? "-"}</td>
                      <td style={{ padding: "10px 12px" }}>{row.SystematicPlanType ?? row.systematicPlanType ?? "-"}</td>
                      <td style={{ padding: "10px 12px" }}>{row.InstallmentAmount ?? row.installmentAmount ?? "-"}</td>
                      <td style={{ padding: "10px 12px" }}>{row.Status ?? row.status ?? "-"}</td>
                      <td style={{ padding: "10px 12px" }}>{row.Remarks ?? row.remarks ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
